import React from 'react';
import RatingItem from '../rlhfRating/RatingItem';

/**
 * One side of a comparison — a full report, rendered by the product's own results view for a
 * simulation OR a read-only transcript for an hcv2 chat session. RatingItem picks the renderer
 * from the side's item ref, so a pair can be sim-vs-sim, chat-vs-chat, or mixed.
 *
 * The OUTPUT a rater judges is the output a customer sees. The INPUTS deliberately differ:
 * `isRatingView` turns on the author-only input filter, and `stackInputs` puts the brief above
 * the report instead of beside it, because a pane is half the width the side-by-side layout was
 * designed for.
 *
 * The A/B badge sits in the pane header and the "this one is better" button in its footer, so the
 * choice can be made against the report it refers to rather than only down in the verdict bar.
 * That button and the verdict bar drive the same state.
 */

interface ReportPaneProps {
  pairId: number;
  side: 'a' | 'b';
  /** The item ref for this side ("42" for a simulation, "chat_9" for a chat session). */
  itemRef: string;
  title: string | null;
  selected: boolean;
  disabled: boolean;
  onPick: () => void;
}

const ReportPane: React.FC<ReportPaneProps> = ({
  pairId, side, itemRef, title, selected, disabled, onPick,
}) => {
  const label = side.toUpperCase();

  return (
    <section
      aria-label={`Report ${label}`}
      className={`bg-white rounded-2xl border transition-all overflow-hidden flex flex-col ${
        selected ? 'border-gray-900 ring-2 ring-gray-900' : 'border-gray-200'
      }`}
    >
      <header className="px-4 py-3 border-b border-gray-200 flex items-center gap-3 min-w-0">
        <span
          aria-hidden="true"
          className={`w-7 h-7 rounded-lg grid place-items-center text-[13px] font-bold shrink-0 ${
            selected ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'
          }`}
        >
          {label}
        </span>
        <p className="font-semibold text-[13px] tracking-tight truncate min-w-0 flex-1">
          {title ?? `Report ${label}`}
        </p>
      </header>

      {/* max-h caps the pane so the two sides stay comparable side by side; the report
          scrolls inside it rather than pushing the verdict controls off screen. */}
      <div className="flex-1 min-h-0 overflow-y-auto max-h-[62vh]">
        <RatingItem
          key={`${pairId}-${side}`}
          keyId={pairId}
          itemRef={itemRef}
          ratingEndpointPath={`/rlhf/pairs/${pairId}/sides/${side}/simulation`}
          stackInputs
        />
      </div>

      <footer className="px-4 py-3 border-t border-gray-200 bg-gray-50/60">
        <button
          type="button"
          onClick={onPick}
          disabled={disabled}
          aria-pressed={selected}
          className={`w-full min-h-[44px] rounded-xl border-[1.5px] text-[13px] font-semibold
                      transition-all hover:-translate-y-px focus:outline-none focus:ring-2
                      focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-50 ${
            selected
              ? 'bg-gray-900 border-gray-900 text-white'
              : 'bg-white border-gray-200 text-gray-700 hover:border-blue-600'
          }`}
        >
          {selected ? `✓ ${label} is better` : `${label} is better`}
        </button>
      </footer>
    </section>
  );
};

export default ReportPane;
